import React, { forwardRef, ImgHTMLAttributes, PropsWithChildren } from 'react';
import styled, { createGlobalStyle } from 'styled-components';

export const ThermalPrintStyle = createGlobalStyle`
  @media print {
    @page {
      size: 80mm auto;
      margin: 0;
    }

    body * {
      visibility: hidden;
    }

    #thermal-receipt,
    #thermal-receipt * {
      visibility: visible;
    }

    #thermal-receipt {
      position: absolute;
      left: 0;
      top: 0;
      width: 80mm;
      padding: 4mm 6mm;
      font-family: 'Courier New', monospace;
    }
  }
`;

const ReceiptContainer = styled.div`
  text-align: left;
  margin-top: 30px;
  width: 30%;

  @media (max-width: 1024px) {
    width: 50%;
  }

  @media (max-width: 768px) {
    width: 80%;
  }
`;

export const Container = ({
  children,
  ...props
}: PropsWithChildren<React.HTMLAttributes<HTMLDivElement>>): JSX.Element => {
  return React.createElement(ReceiptContainer, props, children);
};

const ReceiptContent = styled.div`
  align-items: center;
  margin-bottom: 25px;
  @media print {
    width: 72mm;
    padding: 2mm;
    font-family: 'Courier New', monospace;
    font-size: 12px;
    line-height: 1.4;
  }
`;

export const Content = forwardRef<
  HTMLDivElement,
  PropsWithChildren<React.HTMLAttributes<HTMLDivElement>>
>((props, ref) => {
  return React.createElement(ReceiptContent, { ...props, ref }, props.children);
});

export const Row = styled.div`
  border-bottom: 1px solid gray;
  padding: 3px;
  margin-bottom: 15px;
`;

export const Code = styled.div`
  margin-top: 20px;
`;

const ReceiptLogo = styled.img`
  height: 25px;
  width: 75px;
`;

export const Logo = (
  props: ImgHTMLAttributes<HTMLImageElement>,
): JSX.Element => {
  return React.createElement(ReceiptLogo, props);
};

export const Title = styled.div`
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 5px;
  color: #424242;

  @media print {
    font-size: 13px;
    text-align: center;
    margin-bottom: 2px;
  }
`;

export const Text = styled.div`
  font-size: 14px;
  color: #424242;
  font-weight: bold;
  margin-bottom: 5px;
`;

export const Date = styled.text`
  text-align: left;
  font-size: 13px;
  color: #9e9e9e;
  font-weight: 150;
`;

export const Value = styled.div`
  font-size: 20px;
  text-align: right;
  color: #424242;

  @media print {
    font-size: 14px;
    font-weight: bold;
  }
`;

export const Extract = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 5px;

  @media print {
    margin-bottom: 2px;
  }
`;

export const Subtitle = styled.text`
  font-size: 14px;
  color: #424242;
  padding: 5px;

  @media print {
    font-size: 11px;
    padding: 1px;
  }
`;

export const Data = styled.div`
  font-size: 14px;
  text-align: right;
  color: #424242;
  padding: 5px;

  @media print {
    font-size: 11px;
    padding: 1px;
  }
`;

export const Footer = styled.div`
  align-items: center;
  flex-direction: column;
  text-align: center;

  @media print {
    display: none;
  }
`;

export const Buttons = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 10px;
`;

export const Printable = styled.div`
  flex: 1;
`;
